import {
  executeGraphqlRequest,
  readCachedGraphqlQuery,
} from '../apollo/graphql-request';

export interface ProviderAnalyticsSummary {
  profileViews: number;
  servicesListViews: number;
  serviceViews: number;
  contactClicks: number;
  socialClicks: number;
  matchedProfileImpressions: number;
  matchedServiceImpressions: number;
  matchedProfileClicks: number;
  matchedServiceClicks: number;
  uniqueVisitors: number;
  sessions: number;
}

export interface ProviderAnalyticsDailyPoint {
  date: string;
  profileViews: number;
  serviceViews: number;
  contactClicks: number;
  socialClicks: number;
  matchedProfileClicks: number;
  matchedServiceClicks: number;
  uniqueVisitors: number;
  sessions: number;
}

export interface ProviderAnalyticsTopService {
  serviceUuid: string;
  title: string;
  viewCount: number;
  matchedClickCount: number;
}

export interface ProviderAnalyticsTopCity {
  city: string;
  viewCount: number;
  uniqueVisitors: number;
  sessions: number;
}

export interface ProviderAnalyticsDashboard {
  days: number;
  summary: ProviderAnalyticsSummary;
  daily: ProviderAnalyticsDailyPoint[];
  topServices: ProviderAnalyticsTopService[];
  topCities: ProviderAnalyticsTopCity[];
}

interface ProviderAnalyticsQueryResponse {
  providerAnalytics: ProviderAnalyticsDashboard;
}

const PROVIDER_ANALYTICS_QUERY = `
      query ProviderAnalytics($profileUuid: ID!, $days: Int!) {
        providerAnalytics(profileUuid: $profileUuid, days: $days) {
          days
          summary {
            profileViews
            servicesListViews
            serviceViews
            contactClicks
            socialClicks
            matchedProfileImpressions
            matchedServiceImpressions
            matchedProfileClicks
            matchedServiceClicks
            uniqueVisitors
            sessions
          }
          daily {
            date
            profileViews
            serviceViews
            contactClicks
            socialClicks
            matchedProfileClicks
            matchedServiceClicks
            uniqueVisitors
            sessions
          }
          topServices {
            serviceUuid
            title
            viewCount
            matchedClickCount
          }
          topCities {
            city
            viewCount
            uniqueVisitors
            sessions
          }
        }
      }
    `;

export async function fetchProviderAnalytics(
  profileUuid: string,
  days: 7 | 30 | 90,
  accessToken?: string
): Promise<ProviderAnalyticsDashboard> {
  const data = await executeGraphqlRequest<ProviderAnalyticsQueryResponse>(
    PROVIDER_ANALYTICS_QUERY,
    {
      profileUuid,
      days,
    },
    {
      accessToken,
      requestName: 'ProviderAnalytics',
    }
  );

  return data.providerAnalytics;
}

export function readCachedProviderAnalytics(
  profileUuid: string,
  days: 7 | 30 | 90
): ProviderAnalyticsDashboard | undefined {
  const cachedData = readCachedGraphqlQuery<ProviderAnalyticsQueryResponse>(
    PROVIDER_ANALYTICS_QUERY,
    {
      profileUuid,
      days,
    }
  );

  return cachedData?.providerAnalytics;
}
